import { useEffect } from 'react';
import { artwork, links, release } from '../assets';
import { Artwork } from '../components/Artwork';
import { Button } from '../components/Button';
import { Body, Screen, ScreenBody, Title } from '../components/Screen';
import { copy } from '../copy';
import { timing, type AppConfig } from '../config';

/**
 * Shared: web step 12, kiosk step 8.
 *
 * Kiosk auto-resets to the attract loop after a dwell. It never blocks on SMS
 * delivery — the guest has already been told their clip is on its way and the
 * queue owns making that true, so there is nothing here to wait for.
 */
export function EndCardScreen({
  config,
  onRestart,
}: {
  config: AppConfig;
  onRestart: () => void;
}) {
  const kiosk = config.mode === 'kiosk';

  useEffect(() => {
    if (!kiosk) return;
    const t = window.setTimeout(onRestart, timing.endCardMs);
    return () => clearTimeout(t);
  }, [kiosk, onRestart]);

  return (
    <Screen
      kiosk={kiosk}
      backdrop={
        <Artwork sources={kiosk ? artwork.endCardKiosk : artwork.endCardWeb} alt="The Incomer" />
      }
      testId="endcard"
    >
      <div className="pt-12" />

      {/*
        The poster is the screen, and the copy and the buttons share one scrim
        panel at the foot. They were separate, which left the ghost "Again" set
        in ink/60 directly over the poster's dark billing block, effectively
        invisible.

        No "The Incomer" heading on web: the art says it, three inches above,
        in the campaign's own lockup. The kiosk heading stays because "Check
        your phone" is information the art cannot carry.
      */}
      <ScreenBody className="justify-end">
        <div
          className={`w-full space-y-3 rounded-3xl bg-bone/90 px-5 py-6 backdrop-blur-[2px] ${
            kiosk ? 'max-w-xl' : 'max-w-sm'
          }`}
        >
          {kiosk ? (
            <>
              <Title kiosk>{copy.endCard.headingKiosk}</Title>
              <Body kiosk>{copy.phone.queued}</Body>
            </>
          ) : null}

          {/*
            Once, not twice. This used to render copy.endCard.body — which was
            itself the release line — and then repeat the same sentence in caps
            directly underneath it.
          */}
          <p
            className={`font-display uppercase tracking-[0.3em] text-blood ${
              kiosk ? 'text-4xl' : 'text-lg'
            }`}
          >
            {release.line}
          </p>

          {kiosk ? (
            <p className="text-center text-2xl text-ink/50">{copy.endCard.kioskFooter}</p>
          ) : (
            <div className="space-y-3 pt-2">
              <a href={links.tickets} target="_blank" rel="noreferrer" className="block">
                <Button onArt testId="endcard-tickets">{copy.endCard.tickets}</Button>
              </a>
              <Button variant="ghost" onArt onClick={onRestart}>
                {copy.endCard.again}
              </Button>
            </div>
          )}
        </div>
      </ScreenBody>
    </Screen>
  );
}
